// src/components/Layout/MainLayout.jsx
import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  Container,
  Badge,
  Chip,
  Divider,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Logout,
  ShoppingCart,
  Home,
  LocalGroceryStore,
  Help,
  Info,
  ContactMail,
  Notifications,
  Person,
  Settings,
  Inventory,
  Receipt,
  Message,
  BarChart,
  LocalShipping,
} from '@mui/icons-material';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import logo from '../../assets/logo.jpeg';

const MainLayout = ({ showBuyerNav = false, showSellerNav = false }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notifications] = useState([
    { id: 1, text: 'New order received', time: '2 hours ago', read: false },
    { id: 2, text: 'Your verification is approved', time: '1 day ago', read: true },
    { id: 3, text: 'Payment received for Order #789', time: '3 days ago', read: true },
  ]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, loading: authLoading } = useAuth();

  /* ── Scroll Effect ── */
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /* ── Handlers ── */
  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleNotificationOpen = (e) => setNotificationAnchor(e.currentTarget);
  const handleNotificationClose = () => setNotificationAnchor(null);

  const handleLogout = async () => {
    try {
      setLoading(true);
      logout();
      handleMenuClose();
      toast.success('Logged out successfully');
      navigate('/');
    } catch {
      toast.error('Logout failed');
    } finally {
      setLoading(false);
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  /* ── Navigation Items ── */
  const publicNavItems = [
    { text: 'Home',        path: '/',            icon: <Home /> },
    { text: 'Fruits',      path: '/fruits',       icon: <LocalGroceryStore /> },
    { text: 'How It Works',path: '/how-it-works', icon: <Help /> },
    { text: 'About Us',    path: '/about',        icon: <Info /> },
    { text: 'Contact',     path: '/contact',      icon: <ContactMail /> },
  ];

  const buyerNavItems = [
    { text: 'Dashboard',    path: '/buyer/dashboard',    icon: <Dashboard /> },
    { text: 'Browse Fruits',path: '/fruits',              icon: <LocalGroceryStore /> },
    { text: 'My Enquiries', path: '/buyer/enquiries',     icon: <ShoppingCart /> },
    { text: 'Invoices',     path: '/buyer/invoices',      icon: <Receipt /> },
    { text: 'KYC / Verify', path: '/buyer/verification',  icon: <Person /> },
    { text: 'Messages',     path: '/buyer/messages',      icon: <Message /> },
  ];

  const sellerNavItems = [
    { text: 'Dashboard',    path: '/seller/dashboard',    icon: <Dashboard /> },
    { text: 'Products',     path: '/seller/products',     icon: <Inventory /> },
    { text: 'Orders',       path: '/seller/orders',       icon: <Receipt /> },
    { text: 'Shipments',    path: '/seller/shipments',    icon: <LocalShipping /> },
    { text: 'Invoices',     path: '/seller/invoices',     icon: <Receipt /> },
    { text: 'KYC / Verify', path: '/seller/verification', icon: <Person /> },
    { text: 'Messages',     path: '/seller/messages',     icon: <Message /> },
  ];

  const activeNavItems = showSellerNav
    ? sellerNavItems
    : showBuyerNav
    ? buyerNavItems
    : publicNavItems;

  /* ── User Helpers ── */
  const getUserInitial = () => {
    if (!user) return '?';
    return (user.company_name || user.name || user.email || 'U').charAt(0).toUpperCase();
  };

  const getUserDisplayName = () => {
    if (!user) return 'Guest';
    return user.company_name || user.name || user.email || 'User';
  };

  const getUserRole = () => (user?.user_type ? user.user_type.toUpperCase() : null);

  const getUserRoleColor = () =>
    user?.user_type === 'seller' ? 'secondary' : 'primary';

  const getDashboardPath = () => {
    if (!user) return '/';
    switch (user.user_type) {
      case 'admin':  return '/admin/dashboard';
      case 'seller': return '/seller/dashboard';
      default:       return '/buyer/dashboard';
    }
  };

  const getProfilePath = () => {
    switch (user?.user_type) {
      case 'admin':  return '/admin/profile';
      case 'seller': return '/seller/profile';
      default:       return '/buyer/profile';
    }
  };

  const getSettingsPath = () => {
    switch (user?.user_type) {
      case 'admin':  return '/admin/settings';
      case 'seller': return '/seller/settings';
      default:       return '/buyer/settings';
    }
  };

  /* ── Drawer ── */
  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ width: 280 }}>
      {/* Logo */}
      <Box sx={{ textAlign: 'center', py: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Box
          component="img"
          src={logo}
          alt="Fruits Trading Platform"
          sx={{ height: 50, borderRadius: 1, boxShadow: 2 }}
        />
        {user && getUserRole() && (
          <Box sx={{ mt: 2 }}>
            <Chip
              label={getUserRole()}
              color={getUserRoleColor()}
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          </Box>
        )}
      </Box>

      {/* Nav Links */}
      <List sx={{ px: 2 }}>
        {activeNavItems.map((item) => (
          <ListItem
            key={item.text}
            button
            component={Link}
            to={item.path}
            selected={location.pathname === item.path}
            sx={{
              borderRadius: 2,
              mb: 1,
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'primary.light',
                transform: 'translateX(6px)',
                '& .MuiListItemIcon-root': { color: 'white' },
                '& .MuiListItemText-primary': { color: 'white' },
              },
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                '& .MuiListItemIcon-root': { color: 'white' },
                '& .MuiListItemText-primary': { color: 'white', fontWeight: 'bold' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontWeight: location.pathname === item.path ? 'bold' : 'normal',
              }}
            />
          </ListItem>
        ))}

        <Divider sx={{ my: 1 }} />

        {!user ? (
          <>
            <ListItem button component={Link} to="/login" sx={{ borderRadius: 2, mb: 1 }}>
              <ListItemIcon sx={{ minWidth: 40 }}><Person /></ListItemIcon>
              <ListItemText primary="Login" />
            </ListItem>
            <ListItem
              button
              component={Link}
              to="/register"
              sx={{
                borderRadius: 2,
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': { backgroundColor: 'primary.dark' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'white' }}><Person /></ListItemIcon>
              <ListItemText primary="Register" />
            </ListItem>
          </>
        ) : (
          <>
            <ListItem button component={Link} to={getSettingsPath()} sx={{ borderRadius: 2, mb: 1 }}>
              <ListItemIcon sx={{ minWidth: 40 }}><Settings /></ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItem>
            <ListItem
              button
              onClick={handleLogout}
              disabled={loading}
              sx={{
                borderRadius: 2,
                backgroundColor: 'error.light',
                color: 'white',
                '&:hover': { backgroundColor: 'error.main' },
                '&.Mui-disabled': { opacity: 0.7 },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'white' }}>
                {loading ? <CircularProgress size={20} color="inherit" /> : <Logout />}
              </ListItemIcon>
              <ListItemText primary={loading ? 'Logging out...' : 'Logout'} />
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  /* ── Auth Loading ── */
  if (authLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="body1" color="text.secondary">
          Loading your profile...
        </Typography>
      </Box>
    );
  }

  /* ── Render ── */
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>

      {/* ══════════ HEADER ══════════ */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          backgroundColor: scrolled ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.85)',
          borderBottom: scrolled ? '1px solid rgba(0,0,0,0.08)' : 'none',
          boxShadow: scrolled ? '0 8px 30px rgba(0,0,0,0.08)' : 'none',
          color: '#1a1a1a',
          transition: 'all 0.35s ease',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters>

            {/* Mobile menu button */}
            {isMobile && (
              <IconButton sx={{ color: 'inherit', mr: 2 }} onClick={handleDrawerToggle}>
                <MenuIcon />
              </IconButton>
            )}

            {/* Logo */}
            <Box
              component={Link}
              to={user ? getDashboardPath() : '/'}
              sx={{
                flexGrow: isMobile ? 1 : 0,
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="Fruits Trading Platform"
                sx={{ height: 42, borderRadius: 1, mr: 1 }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  background: 'linear-gradient(45deg, #2e7d32 30%, #4caf50 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                Fruits Trading
              </Typography>
            </Box>

            {/* Desktop Nav */}
            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexGrow: 1, ml: 2, overflow: 'hidden' }}>
                {activeNavItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Tooltip key={item.text} title={item.text} placement="bottom">
                      <Button
                        component={Link}
                        to={item.path}
                        sx={{
                          color: isActive ? 'primary.main' : 'inherit',
                          fontWeight: isActive ? 'bold' : 500,
                          borderRadius: 2,
                          px: { md: 1, lg: 1.5 },
                          py: 0.75,
                          minWidth: 0,
                          fontSize: { md: '0.75rem', lg: '0.85rem' },
                          whiteSpace: 'nowrap',
                          position: 'relative',
                          transition: 'all 0.2s ease',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            left: '50%',
                            bottom: 0,
                            width: isActive ? '70%' : 0,
                            height: '2px',
                            backgroundColor: 'primary.main',
                            transition: 'all 0.3s ease',
                            transform: 'translateX(-50%)',
                          },
                          '&:hover': {
                            color: 'primary.main',
                            backgroundColor: 'rgba(46,125,50,0.08)',
                          },
                          '&:hover::after': { width: '70%' },
                        }}
                      >
                        {/* Show icon only on md, icon+text on lg+ */}
                        <Box sx={{ display: { md: 'none', lg: 'flex' }, alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', fontSize: '1rem' }}>{item.icon}</Box>
                          <span>{item.text}</span>
                        </Box>
                        <Box sx={{ display: { xs: 'none', md: 'flex', lg: 'none' }, alignItems: 'center' }}>
                          {item.icon}
                        </Box>
                      </Button>
                    </Tooltip>
                  );
                })}
              </Box>
            )}

            {/* User Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {user && (
                <IconButton color="inherit" onClick={handleNotificationOpen}>
                  <Badge badgeContent={unreadCount} color="error">
                    <Notifications />
                  </Badge>
                </IconButton>
              )}

              {user ? (
                <>
                  <Button
                    component={Link}
                    to={getDashboardPath()}
                    startIcon={<Dashboard />}
                    sx={{ color: 'inherit', display: { xs: 'none', md: 'flex' } }}
                  >
                    Dashboard
                  </Button>

                  <IconButton onClick={handleMenuOpen} sx={{ ml: 1 }}>
                    {loading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <Avatar
                        sx={{
                          bgcolor:
                            getUserRoleColor() === 'secondary'
                              ? 'secondary.main'
                              : 'primary.main',
                          width: 40,
                          height: 40,
                        }}
                      >
                        {getUserInitial()}
                      </Avatar>
                    )}
                  </IconButton>

                  {getUserRole() && (
                    <Chip
                      label={getUserRole()}
                      color={getUserRoleColor()}
                      size="small"
                      sx={{ fontWeight: 'bold', display: { xs: 'none', sm: 'inline-flex' } }}
                    />
                  )}
                </>
              ) : (
                !isMobile && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button component={Link} to="/login" variant="outlined" sx={{ borderRadius: 2 }}>
                      Login
                    </Button>
                    <Button component={Link} to="/register" variant="contained" sx={{ borderRadius: 2 }}>
                      Register
                    </Button>
                  </Box>
                )
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* ══════════ MOBILE DRAWER ══════════ */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: 280, boxSizing: 'border-box' },
        }}
      >
        {drawer}
      </Drawer>

      {/* ══════════ NOTIFICATIONS MENU ══════════ */}
      <Menu
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={handleNotificationClose}
        PaperProps={{ sx: { mt: 1.5, width: 320, maxHeight: 400 } }}
      >
        <MenuItem sx={{ fontWeight: 'bold', borderBottom: 1, borderColor: 'divider' }}>
          Notifications
          {unreadCount > 0 && (
            <Chip label={`${unreadCount} new`} color="error" size="small" sx={{ ml: 1 }} />
          )}
        </MenuItem>
        {notifications.map((n) => (
          <MenuItem
            key={n.id}
            sx={{
              borderLeft: n.read ? 'none' : '4px solid',
              borderColor: 'primary.main',
              backgroundColor: n.read ? 'inherit' : 'action.hover',
            }}
          >
            <Box sx={{ width: '100%' }}>
              <Typography variant="body2">{n.text}</Typography>
              <Typography variant="caption" color="text.secondary">{n.time}</Typography>
            </Box>
          </MenuItem>
        ))}
        <MenuItem sx={{ justifyContent: 'center', borderTop: 1, borderColor: 'divider' }}>
          <Button size="small">View All</Button>
        </MenuItem>
      </Menu>

      {/* ══════════ USER MENU ══════════ */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{ sx: { mt: 1.5, minWidth: 200 } }}
      >
        <MenuItem sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle2" fontWeight="bold">
              {getUserDisplayName()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
            {getUserRole() && (
              <Chip
                label={getUserRole()}
                color={getUserRoleColor()}
                size="small"
                sx={{ mt: 0.5 }}
              />
            )}
          </Box>
        </MenuItem>

        <MenuItem component={Link} to={getProfilePath()} onClick={handleMenuClose}>
          <ListItemIcon><Person fontSize="small" /></ListItemIcon>
          My Profile
        </MenuItem>

        <MenuItem component={Link} to={getSettingsPath()} onClick={handleMenuClose}>
          <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
          Settings
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={handleLogout}
          disabled={loading}
          sx={{
            color: 'error.main',
            '&:hover': { backgroundColor: 'error.light' },
            '&.Mui-disabled': { opacity: 0.7 },
          }}
        >
          <ListItemIcon sx={{ color: 'inherit' }}>
            {loading ? <CircularProgress size={16} color="inherit" /> : <Logout fontSize="small" />}
          </ListItemIcon>
          {loading ? 'Logging out...' : 'Logout'}
        </MenuItem>
      </Menu>

      {/* ══════════ TOOLBAR SPACER ══════════ */}
      <Toolbar />

      {/* ══════════ MAIN CONTENT ══════════ */}
      <Box component="main" sx={{ flex: 1 }}>
        <Outlet />
      </Box>

      {/* ══════════ FOOTER ══════════ */}
      <Box
        component="footer"
        sx={{
          py: 4,
          backgroundColor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          mt: 'auto',
        }}
      >
        <Container maxWidth="xl">
          {/* Footer grid using flexbox to avoid MUI Grid version issues */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 4,
              mb: 3,
            }}
          >
            {/* Brand */}
            <Box sx={{ flex: '1 1 250px', minWidth: 200 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  component="img"
                  src={logo}
                  alt="Fruits Trading Platform"
                  sx={{ height: 40, borderRadius: 1, mr: 2 }}
                />
                <Typography variant="h6" fontWeight="bold" color="primary">
                  Fruits Trading Platform
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Connecting global fruit traders. Buy and sell fresh fruits from around the world.
              </Typography>
            </Box>

            {/* Buyers */}
            <Box sx={{ flex: '1 1 120px' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Buyers
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Button component={Link} to="/fruits" size="small" sx={{ justifyContent: 'flex-start', px: 0 }}>
                  Browse Fruits
                </Button>
                <Button component={Link} to="/how-it-works" size="small" sx={{ justifyContent: 'flex-start', px: 0 }}>
                  How to Buy
                </Button>
                <Button component={Link} to="/register?type=buyer" size="small" sx={{ justifyContent: 'flex-start', px: 0 }}>
                  Become a Buyer
                </Button>
              </Box>
            </Box>

            {/* Sellers */}
            <Box sx={{ flex: '1 1 120px' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Sellers
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Button component={Link} to="/register?type=seller" size="small" sx={{ justifyContent: 'flex-start', px: 0 }}>
                  Become a Seller
                </Button>
                <Button component={Link} to="/how-it-works" size="small" sx={{ justifyContent: 'flex-start', px: 0 }}>
                  How to Sell
                </Button>
                <Button component={Link} to="/about" size="small" sx={{ justifyContent: 'flex-start', px: 0 }}>
                  Seller Benefits
                </Button>
              </Box>
            </Box>

            {/* Company */}
            <Box sx={{ flex: '1 1 120px' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Company
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Button component={Link} to="/about" size="small" sx={{ justifyContent: 'flex-start', px: 0 }}>
                  About Us
                </Button>
                <Button component={Link} to="/contact" size="small" sx={{ justifyContent: 'flex-start', px: 0 }}>
                  Contact
                </Button>
                <Button component={Link} to="/terms" size="small" sx={{ justifyContent: 'flex-start', px: 0 }}>
                  Terms
                </Button>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} Fruits Trading Platform. All rights reserved.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Built with ❤️ by{' '}
              <Box
                component="a"
                href="https://seo9ine.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'primary.main',
                  fontWeight: 700,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                seo9ine
              </Box>
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default MainLayout;